/**
 * The live camera — three Reanimated shared values.
 *
 * THE core re-render rule of the engine: the camera lives on the UI thread,
 * NEVER in React/Zustand state. Gestures mutate `{tx, ty, zoom}` per frame;
 * the canvas group transform derives from them; React renders ZERO times
 * during pan/zoom. The JS thread only ever reads a SNAPSHOT (on gesture
 * settle) to run culling / LOD / hit-testing.
 *
 * @module floorplan/viewport/useFloorCamera
 * @category FloorPlan
 */

import type { SharedValue } from 'react-native-reanimated';
import { useSharedValue, withTiming } from 'react-native-reanimated';

import type { CameraState } from '../models/camera';
import type { Bounds, ViewportSize } from '../models/geometry';
import { cameraToFitBounds } from '../utils/camera';

export type FloorCamera = {
  tx: SharedValue<number>;
  ty: SharedValue<number>;
  zoom: SharedValue<number>;
};

export const useFloorCamera = (): FloorCamera => {
  const tx = useSharedValue(0);
  const ty = useSharedValue(0);
  const zoom = useSharedValue(1);
  return { tx, ty, zoom };
};

/** JS-thread snapshot for culling / LOD / hit-testing. */
export const cameraSnapshot = (camera: FloorCamera): CameraState => ({
  tx: camera.tx.get(),
  ty: camera.ty.get(),
  zoom: camera.zoom.get(),
});

/** Imperatively move the camera (JS thread), optionally animated. */
export const applyCameraState = (
  camera: FloorCamera,
  state: CameraState,
  options?: { animated?: boolean; durationMs?: number },
): void => {
  const animated = options?.animated ?? true;
  const duration = options?.durationMs ?? 250;
  if (animated) {
    camera.tx.set(withTiming(state.tx, { duration }));
    camera.ty.set(withTiming(state.ty, { duration }));
    camera.zoom.set(withTiming(state.zoom, { duration }));
    return;
  }
  camera.tx.set(state.tx);
  camera.ty.set(state.ty);
  camera.zoom.set(state.zoom);
};

/** Zoom-to-fit arbitrary world bounds (plan fit, zoom-to-selection). */
export const zoomCameraToBounds = (
  camera: FloorCamera,
  bounds: Bounds,
  viewport: ViewportSize,
  options?: { animated?: boolean },
): CameraState => {
  const state = cameraToFitBounds(bounds, viewport);
  applyCameraState(camera, state, options);
  return state;
};
